import logger from './logger';

// Global error handler state
let showingErrorModal = false;
let errorHandler = null;

export const setGlobalErrorHandler = (handler) => {
  errorHandler = handler;
};

// Global error boundary for unhandled errors
export const setupGlobalErrorHandling = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection:', event.reason);
    
    const error = event.reason;
    
    // Handle network errors gracefully
    if (error?.name === 'TypeError' && error?.message?.includes('Failed to fetch')) {
      event.preventDefault(); // Prevent the error from appearing in console
      showNetworkError();
      return;
    }
    
    // Handle authentication errors
    if (error?.response?.status === 401 || error?.response?.status === 403 ||
        error?.message?.includes('SESSION_EXPIRED') || error?.message?.includes('expired')) {
      event.preventDefault();
      if (errorHandler) {
        try {
          errorHandler(error);
        } catch (e) {
          // If error handler fails, show generic error
          showGenericError();
        }
      } else {
        showGenericError();
      }
      return;
    }
    
    // For other errors, let React's error boundary handle them
    logger.error('Unhandled error will be passed to React error boundary:', error);
  });

  // Handle runtime errors
  window.addEventListener('error', (event) => {
    logger.error('Global error caught:', event.error);
    
    const error = event.error;
    
    // Handle network errors
    if (error?.name === 'TypeError' && error?.message?.includes('Failed to fetch')) {
      event.preventDefault();
      showNetworkError();
      return;
    }
    
    // Let other errors bubble up to React error boundaries
  });
};

const showNetworkError = () => {
  if (showingErrorModal) return; // Prevent multiple modals
  
  showingErrorModal = true;
  
  // Create a simple modal
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  `;
  
  modal.innerHTML = `
    <div style="
      background-color: white;
      border-radius: 12px;
      padding: 40px;
      max-width: 450px;
      width: 90%;
      text-align: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    ">
      <div style="font-size: 48px; margin-bottom: 20px;">📡</div>
      <h2 style="margin: 0 0 16px 0; color: #333; font-size: 24px; font-weight: 600;">
        Connection Problem
      </h2>
      <p style="margin: 0 0 30px 0; color: #666; font-size: 16px; line-height: 1.5;">
        We're having trouble connecting to our servers. This could be due to:
      </p>
      <ul style="text-align: left; color: #666; font-size: 14px; margin: 0 0 30px 0; padding-left: 20px;">
        <li style="margin-bottom: 8px;">A temporary network issue</li>
        <li style="margin-bottom: 8px;">Server maintenance</li>
        <li style="margin-bottom: 8px;">Your internet connection</li>
      </ul>
      <button id="retry-button" style="
        background-color: #2980b9;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 12px 32px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s ease;
        width: 100%;
        margin-bottom: 10px;
      ">
        Try Again
      </button>
      <button id="close-button" style="
        background-color: #95a5a6;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 8px 32px;
        font-size: 14px;
        cursor: pointer;
        width: 100%;
      ">
        Close
      </button>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  // Add event listeners
  modal.querySelector('#retry-button').addEventListener('click', () => {
    window.location.reload();
  });
  
  modal.querySelector('#close-button').addEventListener('click', () => {
    document.body.removeChild(modal);
    showingErrorModal = false;
  });
  
  // Remove modal after 10 seconds
  setTimeout(() => {
    if (document.body.contains(modal)) {
      document.body.removeChild(modal);
      showingErrorModal = false;
    }
  }, 10000);
};

const showGenericError = () => {
  if (showingErrorModal) return;
  
  showingErrorModal = true;
  
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  `;
  
  modal.innerHTML = `
    <div style="
      background-color: white;
      border-radius: 12px;
      padding: 40px;
      max-width: 450px;
      width: 90%;
      text-align: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    ">
      <div style="font-size: 48px; margin-bottom: 20px;">⚠️</div>
      <h2 style="margin: 0 0 16px 0; color: #333; font-size: 24px; font-weight: 600;">
        Something went wrong
      </h2>
      <p style="margin: 0 0 30px 0; color: #666; font-size: 16px; line-height: 1.5;">
        An unexpected error occurred. Please try refreshing the page.
      </p>
      <button id="refresh-button" style="
        background-color: #2980b9;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 12px 32px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        width: 100%;
      ">
        Refresh Page
      </button>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  modal.querySelector('#refresh-button').addEventListener('click', () => {
    window.location.reload();
  });
  
  setTimeout(() => {
    if (document.body.contains(modal)) {
      document.body.removeChild(modal);
      showingErrorModal = false;
    }
  }, 8000);
}; 